import { useEffect, useRef, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { DocStatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileText, Eye, RefreshCw } from "lucide-react";

function UploadDialog({ category, existing, open, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [issueDate, setIssueDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setIssueDate("");
      setExpiryDate("");
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please choose a file to upload");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", category.key);
      fd.append("issue_date", issueDate);
      fd.append("expiry_date", expiryDate);
      fd.append("file", file);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${category.label} uploaded — pending TAG Xpress review`);
      onUploaded();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={`upload-dialog-${category.key}`}>
        <DialogHeader>
          <DialogTitle className="font-heading">{existing ? "Replace" : "Upload"}: {category.label}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleUpload} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`file-${category.key}`}>Document file (PDF, JPG, PNG — max 10MB)</Label>
            <Input id={`file-${category.key}`} data-testid={`upload-file-input-${category.key}`} ref={fileRef} type="file"
              accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor={`issue-${category.key}`}>Issue date</Label>
              <Input id={`issue-${category.key}`} data-testid={`upload-issue-date-${category.key}`} type="date"
                value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`expiry-${category.key}`}>Expiry date</Label>
              <Input id={`expiry-${category.key}`} data-testid={`upload-expiry-date-${category.key}`} type="date"
                value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
            </div>
          </div>
          <Button data-testid={`upload-submit-${category.key}`} type="submit" disabled={uploading}
            className="w-full bg-brand font-semibold text-white hover:bg-brand-dark">
            <Upload className="mr-2 h-4 w-4" /> {uploading ? "Uploading…" : "Upload document"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function DocumentsPage() {
  const [data, setData] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  const load = () => {
    api.get("/documents").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(load, []);

  if (!data) return <div data-testid="documents-loading" className="text-sm text-muted-foreground">Loading documents…</div>;

  const { categories, documents } = data;
  const activeDoc = activeCategory ? documents[activeCategory.key] : null;

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div>
        <h1 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">Compliance Documents</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Upload each required document. TAG Xpress reviews every upload before it counts toward compliance.
        </p>
      </div>

      <div className="overflow-hidden rounded-md border border-border bg-card">
        <div className="hidden grid-cols-12 gap-4 border-b border-border bg-muted/60 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground md:grid">
          <div className="col-span-4">Document</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Issue / Expiry</div>
          <div className="col-span-2">Admin comments</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {categories.map((cat) => {
          const doc = documents[cat.key];
          const status = doc ? doc.status : "missing";
          return (
            <div key={cat.key} data-testid={`doc-row-${cat.key}`}
              className="grid grid-cols-1 gap-3 border-b border-border px-5 py-4 transition-colors duration-150 last:border-b-0 hover:bg-slate-50 md:grid-cols-12 md:items-center md:gap-4">
              <div className="md:col-span-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-navy" />
                  <span className="text-sm font-semibold text-navy">{cat.label}</span>
                  {cat.mandatory
                    ? <span className="rounded bg-navy/5 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-navy">Required</span>
                    : <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">Optional</span>}
                </div>
                {doc && (
                  <div className="mt-1 pl-6 text-xs text-muted-foreground">
                    {doc.filename} · uploaded {new Date(doc.uploaded_at).toLocaleDateString("en-AU")}
                    {doc.approved_at && ` · approved ${new Date(doc.approved_at).toLocaleDateString("en-AU")}`}
                  </div>
                )}
              </div>
              <div className="md:col-span-2">
                <DocStatusBadge status={status} testid={`doc-status-${cat.key}`} />
              </div>
              <div className="text-xs text-muted-foreground md:col-span-2" data-testid={`doc-dates-${cat.key}`}>
                {doc ? (
                  <>
                    <div>Issued: {doc.issue_date || "—"}</div>
                    <div className={doc.expiry_date && new Date(doc.expiry_date) < new Date() ? "font-semibold text-red-600" : ""}>
                      Expires: {doc.expiry_date || "—"}
                    </div>
                  </>
                ) : "—"}
              </div>
              <div className="text-xs text-muted-foreground md:col-span-2" data-testid={`doc-comment-${cat.key}`}>
                {doc?.admin_comment || "—"}
              </div>
              <div className="flex items-center gap-2 md:col-span-2 md:justify-end">
                {doc && (
                  <Button asChild variant="outline" size="sm" data-testid={`doc-view-${cat.key}`}>
                    <a href={`${API_BASE}/documents/${doc.id}/file`} target="_blank" rel="noopener noreferrer">
                      <Eye className="mr-1 h-3.5 w-3.5" /> View
                    </a>
                  </Button>
                )}
                <Button size="sm" data-testid={`doc-upload-btn-${cat.key}`}
                  className="bg-navy text-white hover:bg-navy-light"
                  onClick={() => setActiveCategory(cat)}>
                  {doc ? <RefreshCw className="mr-1 h-3.5 w-3.5" /> : <Upload className="mr-1 h-3.5 w-3.5" />}
                  {doc ? "Replace" : "Upload"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {activeCategory && (
        <UploadDialog
          category={activeCategory}
          existing={!!activeDoc}
          open={!!activeCategory}
          onClose={() => setActiveCategory(null)}
          onUploaded={load}
        />
      )}
    </div>
  );
}
